// UserInfoTopicEditor.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Badge,
  Input
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';

import { Check, X, Star, Dot, UserPen, ArrowLeftFromLine, Plus } from 'lucide-react';

import './UserInfoTopicEditor.css';
import VipBadge from "../../components/VipBadge";
import CardCarousel from './components/UserInfoTopicEditorCardCarousel';

import { uploadImage, fetchImage } from "utils/images/imageManager";

import home4 from 'assets/images/info/home4.png';
import placeholder_image from "assets/images/info/card-placeholder-medium.png";

import axiosInstance from "utils/axiosInstance";
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";


const UserInfoTopicEditor = ({ t }) => {

  const { infoTopicId } = useParams();
  const navigate = useNavigate();

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [myInfoProfile, setMyInfoProfile] = useState({});
  const [myInfoTopic, setMyInfoTopic] = useState([]);
  const [myInfoQuestionSets, setMyInfoQuestionSets] = useState([]);
  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [isBackgroundUrlLoaded, setIsBackgroundUrlLoaded] = useState(false);
  const [topicImgUrl, setTopicImgUrl] = useState("");
  const [isTopicUrlLoaded, setIsTopicUrlLoaded] = useState(false);
  const [imageMap, setImageMap] = useState(new Map());

  const [topicLoading, setTopicLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);

  // Editing Name and Bio
  const [editingLabel, setEditingLabel] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const descriptionInputRef = useRef(null);
  
  const handleDescriptionChange = (e) => {
    setDescriptionDraft(e.target.value);

    if (descriptionInputRef.current) {
      descriptionInputRef.current.style.height = "auto"; // Reset height
      descriptionInputRef.current.style.height = descriptionInputRef.current.scrollHeight + "px"; // Resize
    }
  };
  useEffect(() => {
    if (editingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.style.height = "auto";
      descriptionInputRef.current.style.height = descriptionInputRef.current.scrollHeight + "px";
    }
  }, [editingDescription]);


  //--------------------------------------------------
  // Load userTopic and userQuestionSets on mount
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setTopicLoading(true);
      setDataFetchError(null);

      // Load Topic and QuestionSets
      const [profileResponse, topicResponse, questionSetResponse] = await Promise.all([
        axiosInstance.get(`/knovia/myInfo/userInfoProfile`),
        axiosInstance.get(`/knovia/myInfo/${infoTopicId}/getUserTopicInfo`),
        axiosInstance.get(`/knovia/myInfo/${infoTopicId}/getQuestionSetsInUserTopic`)
      ]);

      console.log('Profile Loaded:', profileResponse.data);
      setMyInfoProfile(profileResponse.data);

      console.log('Topic Loaded:', topicResponse.data);
      setMyInfoTopic(topicResponse.data);

      console.log('QuestionSets in Topic Loaded:', questionSetResponse.data);

      // Sort by `order` before setting state
      const sortedQuestionSets = [...questionSetResponse.data].sort(
        (a, b) => a.order - b.order
      );
      setMyInfoQuestionSets(sortedQuestionSets);

      return {
        profile: profileResponse.data,
        topic: topicResponse.data,
        sortedQuestionSets,
      };
    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load data.");
      return null; // <- This ensures the caller knows the fetch failed
    } finally {
      setTopicLoading(false);
    }
  };

  const createdObjectUrls = new Set(); // track urls
  const fetchTopicImages = async (profile, topic) => {
    try {

      const backgroundImageName = topic?.backgroundImage || profile.backgroundImage;
      const topicImageName = topic?.image;

      const topicBackgroundPromise = backgroundImageName
        ? fetchImage(backgroundImageName, "original")
        : Promise.resolve(null);

      const topicImgPromise = topic?.image
        ? fetchImage(topic.image, "original")
        : Promise.resolve(null);

      if (!backgroundImageName) setIsBackgroundUrlLoaded(true);
      if (!topicImageName) setIsTopicUrlLoaded(true);

      const [backgroundRes, topicRes] = await Promise.all([
        topicBackgroundPromise,
        topicImgPromise,
      ]);

      setIsBackgroundUrlLoaded(true);
      setIsTopicUrlLoaded(true);

      if (backgroundRes?.blob) {
        const bgUrl = URL.createObjectURL(backgroundRes.blob);
        createdObjectUrls.add(bgUrl); // track for cleanup
        setBackgroundUrl(bgUrl);
      }

      if (topicRes?.blob) {
        const topicUrl = URL.createObjectURL(topicRes.blob);
        createdObjectUrls.add(topicUrl); // track for cleanup
        setTopicImgUrl(topicUrl);
      }
    } catch (err) {
      console.error("Error fetching topic images:", err);
    }
  };


  const fetchQuestionSetThumbnails = async (sortedQuestionSets) => {
    const newMap = new Map(imageMap); // preserve existing

    await Promise.all(
      sortedQuestionSets.map(async (entry) => {
        const filename = entry.image;
        if (!filename || newMap.has(filename)) return;

        try {
          const { blob } = await fetchImage(filename, "original");
          const objectUrl = URL.createObjectURL(blob);
          createdObjectUrls.add(objectUrl); // track for cleanup
          newMap.set(filename, objectUrl);
        } catch (e) {
          console.error("Failed to load image:", filename);
        }
      })
    );
    console.log("newMap:", newMap);
    setImageMap(newMap);

  };

  useEffect(() => {
    return () => {
      for (const url of createdObjectUrls) {
        URL.revokeObjectURL(url);
      }
      createdObjectUrls.clear();
    };
  }, []);

  useEffect(() => {
    (async () => {
      const { profile, topic, sortedQuestionSets } = await fetchData();
      if (!topic) return;

      if (topic) {
        fetchQuestionSetThumbnails(sortedQuestionSets);
        fetchTopicImages(profile, topic);
      }
    })();
  }, [infoTopicId]);


  //--------------------------------------------------
  // Editing Images
  //--------------------------------------------------

  const handleUploadImageFileChange = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file || !type) return;

    const tempUrl = URL.createObjectURL(file);
    if (type === 'backgroundImage') {
      setBackgroundUrl(tempUrl);
    } else if (type === 'topicImage') {
      setTopicImgUrl(tempUrl);
    }

    try {
      const filename = await uploadImage(file);
      const imgUrl = await fetchImage(filename, "original");

      // Update state with final image URL
      if (type === 'backgroundImage') {
        setBackgroundUrl(imgUrl);
      } else if (type === 'topicImage') {
        setTopicImgUrl(imgUrl);
      }

      // Patch to backend
      await axiosInstance.patch(`/knovia/myInfo/${infoTopicId}/updateUserInfoTopicImage`, {
        [type]: filename,
      });

      setMyInfoTopic((prev) => ({
        ...prev,
        [type]: filename,
      }));

    } catch (err) {
      console.error(`Failed to upload ${type}:`, err);
    }
  };


  //--------------------------------------------------
  // Editing Label and Description
  //--------------------------------------------------

  const handleEditLabel = () => {
    setLabelDraft(myInfoTopic?.label || '');
    setEditingLabel(true);
  };

  const handleSaveLabel = async () => {
    try {
      await axiosInstance.patch(`/knovia/myInfo/${infoTopicId}/updateUserTopicLabel`, {
        label: labelDraft,
      });

      setMyInfoTopic(prev => ({
        ...prev,
        label: labelDraft,
      }));

      setEditingLabel(false);
    } catch (err) {
      console.error("Failed to update label:", err);
    }
  };

  const handleEditDescription = () => {
    setDescriptionDraft(myInfoTopic?.description || '');
    setEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    try {
      await axiosInstance.patch(`/knovia/myInfo/${infoTopicId}/updateUserTopicDescription`, {
        description: descriptionDraft,
      });

      setMyInfoTopic(prev => ({
        ...prev,
        description: descriptionDraft,
      }));

      setEditingDescription(false);
    } catch (err) {
      console.error("Failed to update description:", err);
    }
  };


  //--------------------------------------------------
  // Adding User Question Set
  //--------------------------------------------------

  const handleAddNewQuestionSet = async () => {
    try {
      const res = await axiosInstance.post(`/knovia/myInfo/${infoTopicId}/userInfoQuestionSet`);
      const newQuestionSet = res.data;

      setMyInfoQuestionSets(prev => [...prev, newQuestionSet]);
    } catch (err) {
      console.error("Failed to create new question set:", err);
      alert("新增題組失敗，請稍後再試");
    }
  };


  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content user-info-topic" 
      style={{ "--page-content-background": `url(${home4})` }}
    >

      <Container fluid className="user-info-topic-container">

        <div className="d-block d-md-none">
          {/* Background Image at top */}
          <div 
            className={`user-info-topic-top-image ${isBackgroundUrlLoaded ? 'fade-in' : ''}`}
            style={{
              backgroundImage: `url(${backgroundUrl || placeholder_image})`,
              cursor: 'pointer',
            }}
            onClick={() => document.getElementById('backgroundUpload').click()}
          >
          </div>

          <input
            type="file"
            accept="image/*"
            id="backgroundUpload"
            style={{ display: 'none' }}
            onChange={(e) => handleUploadImageFileChange(e, 'backgroundImage')}
          />

          <div className="user-info-topic-page-back-div">
            <Button
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginRight: "5px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                background: "#55535373"
              }}
              onClick={()=>{ navigate(`/myInfo/profile`); }}
            >
              <ArrowLeftFromLine size={24} />
            </Button>
          </div>
        </div>

        <Card className="user-info-topic-card">
          <CardBody className="user-info-topic-card-body">


            {/*------ Small Screen ------*/}
            <div className="d-block d-md-none">

              {/* Topic Thumbnail */}
              <input
                type="file"
                accept="image/*"
                id="profileUpload"
                style={{ display: 'none' }}
                onChange={(e) => handleUploadImageFileChange(e, 'topicImage')}
              />

              <div
                className={`user-info-topic-thumbnail ${isTopicUrlLoaded ? 'fade-in' : ''}`}
                style={{
                  backgroundImage: `url(${topicImgUrl || placeholder_image})`,
                  cursor: 'pointer',
                }}
                onClick={() => document.getElementById('profileUpload').click()}
              >
              </div>

              {/* label and description */}
              <Row>
                <Col className="info-profile-label">
                  {/* Display Name + Pen */}
                  <div className="d-flex align-items-center"
                    style={{
                      maxWidth: "50%",
                      wordBreak: "break-word",
                      textAlign: "center"
                    }}
                  >
                    {editingLabel ? (
                      <div className="mb-2 d-flex align-items-center">
                        <Input
                          type="text"
                          maxLength={30}
                          value={labelDraft}
                          onChange={(e) => setLabelDraft(e.target.value)}
                          style={{ maxWidth: "300px", fontSize: "18px" }}
                        />
                        <Button size="sm" color="success" className="ms-2" style={{ padding: "5px 5px" }} onClick={handleSaveLabel}>
                          <Check size={16} />
                        </Button>
                        <Button size="sm" color="secondary" className="ms-1" style={{ padding: "5px 5px" }} onClick={() => setEditingLabel(false)}>
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="mb-1 d-inline-block">{myInfoTopic?.label}</p>
                        <UserPen
                          size={16}
                          style={{ marginLeft: 8, cursor: 'pointer', flexShrink: "0" }}
                          onClick={()=>{setEditingDescription(false); handleEditLabel();}}
                        />
                      </>
                    )}
                  </div>

                  {/* Bio + Pen */}
                  <div className="d-flex align-items-center"
                    style={{
                      maxWidth: "70%",
                      wordBreak: "break-word",
                      textAlign: "center"
                    }}
                  >
                    {editingDescription ? (
                      <>
                        <Input
                          type="textarea"
                          maxLength={100}
                          value={descriptionDraft}
                          onChange={handleDescriptionChange}
                          innerRef={descriptionInputRef}
                          rows={1}
                          style={{ maxWidth: "400px", resize: "none", overflow: "hidden" }}
                        />
                        <Button size="sm" color="success" className="ms-2" style={{ padding: "5px 5px" }} onClick={handleSaveDescription}>
                          <Check size={16} />
                        </Button>
                        <Button size="sm" color="secondary" className="ms-1" style={{ padding: "5px 5px" }} onClick={() => setEditingDescription(false)}>
                          <X size={16} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <p
                          className="mb-0"
                          style={{ fontSize: "13.5px", color: "#848282" }}
                        >
                          {myInfoTopic?.description}
                        </p>
                        <UserPen
                          size={16}
                          style={{ marginLeft: 8, cursor: 'pointer', flexShrink: "0" }}
                          onClick={()=>{setEditingDescription(false); handleEditDescription();}}
                        />
                      </>
                    )}
                  </div>

                </Col>
              </Row>

              {/* Star and VIP level */}
              <Row>
                <Col className="user-info-topic-star-col">
                  <div className="d-flex">
                    <p className="mb-0">4.9</p>
                    <Star size={18} fill="#faca82" stroke="none"
                      style={{ margin: '1.8px 2px 0px 2px' }}
                    />
                    {/* A filled star */}
                    <p className="mb-0 text-muted">(400+)</p>
                  </div>
                  <div>
                    <Dot size={16} /> {/* You can also use a styled div for a smaller solid dot */}
                  </div>
                  <div style={{ marginTop: "-6px" }}>
                    <VipBadge vipLevel="VIP1" imageSize={35} textSize={42} />
                  </div>
                </Col>
              </Row>

              <Row style={{ marginBottom: "30px" }}>
                <Col className="user-info-topic-filled-count-col">
                  <Button color="soft-success" style={{
                    height: "30px",
                    paddingTop: "3.5px"
                  }}>
                    1000+ 人已填寫此主題
                  </Button>
                </Col>
              </Row>

            </div>


            {/*------ Big Screen ------*/}
            <div className="d-none d-md-block">
              <Row style={{ 
                paddingTop: "25px",
                paddingBottom: "25px", 
                /*backgroundColor: "#f0f0f0",*/
                background: "linear-gradient(160deg, #91cdff 0%, white 50%)",
                margin: "0", 
                borderRadius: "4px 4px 0 0" 
              }}>
                <Col md={6} className="d-flex justify-content-center">
                  {/* Collection Image at top */}
                  <div className="user-info-topic-top-image"
                    style={{
                      backgroundImage: `url(${backgroundUrl || placeholder_image})`,
                    }}
                  >
                    {/* Topic Thumbnail */}
                    <div className="user-info-topic-thumbnail"
                      style={{
                        backgroundImage: `url(${topicImgUrl || placeholder_image})`,
                      }}
                    >
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  {/* Topic Label */}
                  <Row>
                    <Col className="d-flex flex-column user-info-topic-label"
                      style={{ padding: "15px 0px 0px 0px" }}
                    >
                      <p style={{ marginBottom: "8px" }}>{myInfoTopic?.label}</p>
                      <div style={{ maxWidth: "70%" }}>
                        <p className="mb-0 text-muted" style={{ fontSize:'13.5px' }}>
                          實用的造型工具，透過加熱金屬棒捲曲頭髮，能創造自然捲度與蓬鬆感，是日常快速整理髮型的好幫手。
                        </p>
                      </div>
                    </Col>
                  </Row>

                  {/* Star and VIP level */}
                  <Row>
                    <Col className="quser-info-topic-star-col">
                      <div className="d-flex">
                        <p className="mb-0">4.9</p>
                        <Star size={16} fill="black" strokeWidth="none"
                          style={{ margin: '3px 2px 0px 2px' }}
                        />
                        {/* A filled star */}
                        <p className="mb-0 text-muted">(400+)</p>
                      </div>
                      <div style={{ margin: '0px 2px 0px 5px' }}>
                        <Dot size={16} /> {/* You can also use a styled div for a smaller solid dot */}
                      </div>
                      <div style={{ marginTop: "-6px" }}>
                        <VipBadge vipLevel="VIP1" imageSize={35} textSize={42} />
                      </div>
                    </Col>
                  </Row>

                  <Row style={{ marginBottom: "30px" }}>
                    <Col className="user-info-topic-filled-count-col">
                      <div className="d-flex flex-column flex-lg-row" style={{
                        justifyContent: "space-between", width: "80%"
                      }}>
                        <div className="mb-2 mb-lg-0">
                          <Button color="soft-success" 
                            style={{
                              height: "30px",
                              paddingTop: "3.5px"
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.blur();
                              return false;
                            }}
                          >
                            1000+ 人已填寫此主題
                          </Button>
                        </div>

                        <div className="d-flex user-info-topic-author-div">
                          <UserPen size={24} strokeWidth={1.25} color="#5086b3" />
                          <p className="user-info-topic-author mb-0">TTMY_Admin</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>

            </div>




            <div className="section-div" />

            {/*------ Small Screen ------*/}
            <div className="d-block d-md-none">
              {/* QuestionSet Carousel */}
              <Row>
                <Col className="user-info-topic-filled-carousel-col">
                  <div className="d-flex justify-content-between" style={{ paddingLeft: "35px"}}>
                    <p className="mb-0 user-info-topic-section-title">題組列表</p>

                    <Button
                      color="info"
                      onClick={handleAddNewQuestionSet}
                      style={{
                        width: "45px",
                        height: "45px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginRight: "15px",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                      }}
                    >
                      <Plus size={24} />
                    </Button>
                  </div>
                  <CardCarousel 
                    cards={myInfoQuestionSets}
                    imageMap={imageMap}
                  />
                </Col>
              </Row>

              <div className="section-div" style={{ margin: "30px 0px 0px 0px" }}/>
            </div>


            {/*------ Big Screen ------*/}
            <div className="d-none d-md-block">
              {/* QuestionSet Carousel */}
              <Row style={{ padding: "0px 0px 0px 0px", margin: '0px' }}>
                <Col className="user-info-topic-filled-carousel-col"
                  style={{ padding: "30px 0px 60px 50px" }}
                >
                  <div style={{ paddingLeft: "20px" }}>
                    <p className="mb-0 user-info-topic-section-title">所有題組</p>
                  </div>
                  <CardCarousel 
                    cards={myInfoQuestionSets}
                    imageMap={imageMap}
                  />
                </Col>
              </Row>
            </div>


          </CardBody>
        </Card>

      </Container>
    </div>
  );
};

export default withTranslation()(UserInfoTopicEditor);


